import api, { type RequestOptions } from './api';

// Edge types based on API response
export interface Edge {
  id: string;
  devSerNum: string;
  devName: string;
  devType?: string | number;
  devModel?: string;
  devDesc?: string;
  devLocation?: string;
  devVersion?: string;
  firmwareVersion?: string;
  mode?: string;
  vppMode?: string;
  ip?: string;
  publicIp?: string;
  upInformation?: boolean;
  tenantId: string;
  createTime?: string;
  updateTime?: string;
  createBy?: string;
  updateBy?: string;
  // V2 metadata from edges table (certificate provisioning)
  edgeModel?: string;
  macAddress?: string;
  registrationStatus?: string;
  registeredAt?: string;
  agentVersion?: string;
  /**
   * The API's online verdict (GET /api/v1/edges): a connected
   * `edge_mqtt_connected` sample within 120 s, the same rule as the dashboard
   * count and the edge detail (VSNP-1470). The Edges page badge uses it
   * (VSNP-841).
   */
  isOnline?: boolean;
}

export interface EdgeListParams {
  search?: string;
  tenantId?: string;
  page?: number;
  pageSize?: number;
}

export interface EdgeCreateData {
  devSerNum: string;
  devName: string;
  devType?: number;
  devModel?: string;
  devDesc?: string;
  devLocation?: string;
  tenantId?: string;
}

export interface EdgeUpdateData {
  devName?: string;
  devType?: string;
  devLocation?: string;
  devDesc?: string;
}

export interface PaginatedEdgeResponse {
  items: Edge[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

const edgeService = {
  /**
   * List all edges (auto-filtered by tenant)
   */
  list: async (params?: EdgeListParams, options?: RequestOptions) => {
    const response = await api.get('/edges', { params, ...options });
    return response.data as PaginatedEdgeResponse;
  },

  /**
   * Get edge by ID (validates ownership)
   */
  getById: async (id: string) => {
    const response = await api.get(`/edges/${id}`);
    return response.data as Edge;
  },

  /**
   * Create new edge (assigns tenant)
   */
  create: async (data: EdgeCreateData) => {
    const response = await api.post('/edges', data);
    return response.data as Edge;
  },

  /**
   * Update edge (validates ownership)
   */
  update: async (id: string, data: EdgeUpdateData) => {
    const response = await api.put(`/edges/${id}`, data);
    return response.data as Edge;
  },

  /**
   * Delete edge (validates ownership)
   */
  delete: async (id: string) => {
    await api.delete(`/edges/${id}`);
  },

  // ==========================================
  // Edge Approval & Certificate Provisioning
  // ==========================================

  /**
   * Approve pending edge and get deployment password
   */
  approveEdge: async (guid: string, approvedBy: string, configTemplateId?: string) => {
    const response = await api.post(`/api/v2/edges/${guid}/approve`, { approvedBy, configTemplateId });
    return response as any; // Interceptor already returns response.data
  },

  /**
   * Get edge status for certificate provisioning workflow
   */
  getEdgeStatus: async (guid: string) => {
    const response = await api.get(`/api/v2/edges/${guid}/status`);
    return response as any; // Interceptor already returns response.data
  },

  // ==========================================
  // Pending Edges (Certificate Registration)
  // ==========================================

  /**
   * List pending edges awaiting approval
   */
  listPendingEdges: async () => {
    const response = await api.get('/api/v2/edges/pending');
    return response as any; // Interceptor already returns response.data
  },

  /**
   * Reject pending edge
   */
  rejectEdge: async (guid: string, reason: string) => {
    const response = await api.post(`/api/v2/edges/${guid}/reject`, { reason });
    return response as any; // Interceptor already returns response.data
  },

  /**
   * Delete a single pending/approved edge
   */
  deletePendingEdge: async (guid: string) => {
    const response = await api.delete(`/api/v2/edges/pending/${guid}`);
    return response as any;
  },

  /**
   * Clear all pending and approved edges (bulk delete)
   */
  clearPendingEdges: async () => {
    const response = await api.delete('/api/v2/edges/pending/clear');
    return response as any; // Interceptor already returns response.data
  },

  /**
   * Reset edge configuration
   */
  reset: async (id: string) => {
    const response = await api.post(`/edges/${id}/reset`);
    return response.data;
  },

  /**
   * Force sync edge
   */
  sync: async (id: string) => {
    const response = await api.post(`/edges/${id}/sync`);
    return response.data;
  },

  /**
   * Force configuration synchronization
   * Rebuilds and sends complete configuration via MQTT batch
   */
  syncConfiguration: async (id: string) => {
    const response = await api.post(`/edges/${id}/config/sync`);
    return response.data;
  },

  /**
   * Get config state for an edge (dirty flag, sync status)
   */
  getConfigState: async (id: string, options?: RequestOptions) => {
    const response = await api.get(`/edges/${id}/config-state`, options);
    return response.data;
  },

  /**
   * Get config states for multiple edges (for list view)
   */
  getConfigStates: async (edgeIds: string[], options?: RequestOptions) => {
    const response = await api.post('/edges/config-states', { edgeIds }, options);
    return response.data;
  },

  // ==========================================
  // Health Overview
  // ==========================================

  /**
   * Get edge health quick stats
   */
  getHealthStats: async (id: string) => {
    const response = await api.get(`/edges/${id}/health/stats`);
    return response.data;
  },

  /**
   * Get edge health chart data
   */
  getHealthCharts: async (id: string, timeRange?: string) => {
    const response = await api.get(`/edges/${id}/health/charts`, {
      params: { timeRange },
    });
    return response.data;
  },

  // ==========================================
  // Edge Actions
  // ==========================================

  /**
   * Reboot edge
   */
  reboot: async (id: string) => {
    const response = await api.post(`/edges/${id}/actions/reboot`);
    return response.data;
  },

  // ==========================================
  // Interface Status
  // ==========================================

  /**
   * Get physical interfaces
   */
  getPhysicalInterfaces: async (id: string, filters?: { name?: string; configuredState?: string; operationalState?: string }) => {
    const response = await api.get(`/edges/${id}/interfaces/physical`, { params: filters });
    return response.data;
  },

  /**
   * Get logical interfaces
   */
  getLogicalInterfaces: async (id: string, filters?: { name?: string; configuredState?: string; operationalState?: string }) => {
    const response = await api.get(`/edges/${id}/interfaces/logical`, { params: filters });
    return response.data;
  },

  /**
   * Get the WireGuard interfaces that actually exist on the edge (peering
   * tunnels wg2+, plus wg0/wg1 only when the IoT/App gateways are enabled).
   * VSNP-1010 — replaces the ACL modal's hardcoded wg0/wg1 list.
   * Returns { items: Array<{ name, feature, enabled }> }.
   */
  getWireguardInterfaces: async (id: string) => {
    const response = await api.get(`/edges/${id}/interfaces/wireguard`);
    return response.data;
  },

  /**
   * Toggle physical interface state
   */
  togglePhysicalInterface: async (id: string, interfaceId: string, enabled: boolean) => {
    const response = await api.put(`/edges/${id}/interfaces/physical/${interfaceId}/toggle`, { enabled });
    return response.data;
  },

  /**
   * Get physical interface dependencies (pre-flight check before disable)
   */
  getPhysicalInterfaceDependencies: async (id: string, interfaceId: string) => {
    const response = await api.get(`/edges/${id}/interfaces/physical/${interfaceId}/dependencies`);
    return response.data;
  },

  /**
   * Reconcile physical interfaces from the most recent VPP inform (VSNP-689).
   * Backfills dev_port for any NIC the agent has reported but the orchestrator
   * is missing. Insert-only — never overwrites existing rows.
   */
  reconcilePhysicalInterfaces: async (id: string) => {
    const response = await api.post(`/edges/${id}/interfaces/physical/reconcile`);
    return response.data;
  },

  /**
   * Get logical interface dependencies (pre-flight check before delete)
   */
  getLogicalInterfaceDependencies: async (id: string, interfaceId: string) => {
    const response = await api.get(`/edges/${id}/config/logical-interfaces/${interfaceId}/dependencies`);
    return response.data;
  },

  /**
   * Get config history (last 5 valid snapshots)
   */
  getConfigHistory: async (id: string) => {
    const response = await api.get(`/edges/${id}/config/history`);
    return response.data;
  },

  /**
   * Restore config from snapshot
   */
  restoreConfigSnapshot: async (id: string, snapshotId: number) => {
    const response = await api.post(`/edges/${id}/config/restore/${snapshotId}`);
    return response.data;
  },

  // ==========================================
  // Tunnel Status
  // ==========================================

  // ==========================================
  // Routing Table
  // ==========================================

  /**
   * Get service routing table
   */
  getServiceRoutes: async (id: string) => {
    const response = await api.get(`/edges/${id}/routing/service-routes`);
    return response.data;
  },

  /**
   * Get ARP table
   */
  getARPTable: async (id: string) => {
    const response = await api.get(`/edges/${id}/routing/arp`);
    return response.data;
  },

  // ==========================================
  // Diagnostics
  // ==========================================

  /**
   * Run ping test
   */
  runPing: async (id: string, target: string, count?: number, interval?: number) => {
    const response = await api.post(`/edges/${id}/diagnostics/ping`, { target, count, interval });
    return response.data;
  },

  /**
   * Run traceroute
   */
  runTraceroute: async (id: string, target: string, maxHops?: number) => {
    const response = await api.post(`/edges/${id}/diagnostics/traceroute`, { target, maxHops });
    return response.data;
  },

  /**
   * Run DNS lookup
   */
  runDnsLookup: async (id: string, domain: string, recordType?: string) => {
    const response = await api.post(`/edges/${id}/diagnostics/dns`, { domain, recordType });
    return response.data;
  },

  /**
   * Run port scan
   */
  runPortScan: async (id: string, target: string, startPort?: number, endPort?: number) => {
    const response = await api.post(`/edges/${id}/diagnostics/port-scan`, { target, startPort, endPort });
    return response.data;
  },

  /**
   * Run speed test
   */
  runSpeedTest: async (id: string, server?: string) => {
    const response = await api.post(`/edges/${id}/diagnostics/speed-test`, { server });
    return response.data;
  },

  // ==========================================
  // Configuration - Physical Interfaces
  // ==========================================

  /**
   * Update physical interface configuration
   */
  updatePhysicalInterface: async (id: string, interfaceId: string, config: any) => {
    const response = await api.put(`/edges/${id}/config/physical-interfaces/${interfaceId}`, config);
    return response.data;
  },

  // ==========================================
  // Configuration - Logical Interfaces
  // ==========================================

  /**
   * List logical interfaces configuration
   */
  listLogicalInterfaces: async (id: string) => {
    const response = await api.get(`/edges/${id}/config/logical-interfaces`);
    return response.data;
  },

  /**
   * Create logical interface
   */
  createLogicalInterface: async (id: string, config: any) => {
    const response = await api.post(`/edges/${id}/config/logical-interfaces`, config);
    return response.data;
  },

  /**
   * Atomically create a logical interface plus all of its drafted subscriptions
   * in one transaction. `payload` is `{ interface, subscriptions }`; the API
   * assigns each subscription's logical_port_id to the interface it creates.
   * Either everything commits or nothing does. Returns
   * `{ interface, subscriptions }`.
   */
  commitLogicalInterface: async (
    id: string,
    payload: { interface: any; subscriptions: any[] },
  ) => {
    const response = await api.post(`/edges/${id}/config/logical-interfaces/commit`, payload);
    return response.data;
  },

  /**
   * Update logical interface
   */
  updateLogicalInterface: async (id: string, interfaceId: string, config: any) => {
    const response = await api.put(`/edges/${id}/config/logical-interfaces/${interfaceId}`, config);
    return response.data;
  },

  /**
   * Delete logical interface
   */
  deleteLogicalInterface: async (id: string, interfaceId: string) => {
    await api.delete(`/edges/${id}/config/logical-interfaces/${interfaceId}`);
  },

  // ==========================================
  // Configuration - Subscriptions
  // ==========================================

  /**
   * List subscriptions. When `logicalPortId` is set, returns only the
   * subscriptions attached to that one logical interface.
   */
  listSubscriptions: async (id: string, filters?: { type?: string; filter?: string; logicalPortId?: string }) => {
    const response = await api.get(`/edges/${id}/config/subscriptions`, { params: filters });
    return response.data;
  },

  /**
   * Get address assignment subscriptions (DHCP pools)
   */
  getAddressAssignmentSubscriptions: async (id: string) => {
    const response = await api.get(`/edges/${id}/config/subscriptions/address-assignment`);
    return response.data;
  },

  /**
   * Get address translation subscriptions (NAT rules)
   */
  getAddressTranslationSubscriptions: async (id: string) => {
    const response = await api.get(`/edges/${id}/config/subscriptions/address-translation`);
    return response.data;
  },

  /**
   * Create subscription
   */
  createSubscription: async (id: string, subscription: any) => {
    const response = await api.post(`/edges/${id}/config/subscriptions`, subscription);
    return response.data;
  },

  /**
   * Update subscription
   */
  updateSubscription: async (id: string, subscriptionId: string, subscription: any) => {
    const response = await api.put(`/edges/${id}/config/subscriptions/${subscriptionId}`, subscription);
    return response.data;
  },

  /**
   * Delete subscription
   */
  deleteSubscription: async (id: string, subscriptionId: string) => {
    await api.delete(`/edges/${id}/config/subscriptions/${subscriptionId}`);
  },

  // ==========================================
  // Configuration - Static Routes
  // ==========================================

  /**
   * List static routes
   */
  listStaticRoutes: async (id: string) => {
    const response = await api.get(`/edges/${id}/config/static-routes`);
    return response.data;
  },

  /**
   * List virtual tunnel (WireGuard peering) interfaces selectable as a
   * static-route egress (VSNP-981).
   */
  listTunnelInterfaces: async (id: string) => {
    const response = await api.get(`/edges/${id}/config/tunnel-interfaces`);
    return response.data;
  },

  /**
   * Create static route
   */
  createStaticRoute: async (id: string, route: any) => {
    const response = await api.post(`/edges/${id}/config/static-routes`, route);
    return response.data;
  },

  /**
   * Update static route
   */
  updateStaticRoute: async (id: string, routeId: string, route: any) => {
    const response = await api.put(`/edges/${id}/config/static-routes/${routeId}`, route);
    return response.data;
  },

  /**
   * Delete static route
   */
  deleteStaticRoute: async (id: string, routeId: string) => {
    await api.delete(`/edges/${id}/config/static-routes/${routeId}`);
  },

  // ==========================================
  // SSH Remote Access
  // ==========================================

  /**
   * Get SSH access status for edge
   */
  getSSHStatus: async (guid: string, options?: RequestOptions) => {
    const response = await api.get(`/api/v2/edges/${guid}/ssh-status`, options);
    return response as unknown as SSHStatusResponse;
  },

  /**
   * Generate SSH tunnel key pair for edge
   */
  generateSSHTunnelKey: async (guid: string) => {
    const response = await api.post(`/api/v2/edges/${guid}/ssh-tunnel-key`);
    return response as unknown as SSHTunnelKeyResponse;
  },

  /**
   * Generate SSH host certificate for edge
   */
  generateSSHHostCert: async (guid: string, publicKey: string, principals?: string[]) => {
    const response = await api.post(`/api/v2/edges/${guid}/ssh-host-cert`, {
      publicKey,
      principals,
    });
    return response as unknown as SSHHostCertResponse;
  },

  /**
   * Generate SSH user certificate for admin.
   * Accepts EITHER `publicKey` (legacy paste flow) or `userKeyId`
   * (a saved key from /users/me/ssh-keys — VSNP-680).
   */
  generateSSHUserCert: async (
    keyOrId: { publicKey: string; userKeyId?: undefined } | { userKeyId: string; publicKey?: undefined },
    principals?: string[],
    edgeId?: string,
    // VSNP-680 UX: paste-flow auto-save metadata. The backend will record
    // the pasted key in sys_user_ssh_keys with this label + path so the
    // user can pick it from the saved-key list next time.
    pasteMeta?: { keyLabel?: string; privateKeyPath?: string },
  ) => {
    const response = await api.post('/api/v2/edges/ssh-user-cert', {
      ...keyOrId,
      principals: principals || ['edge', 'root'],
      edgeId,
      ...(pasteMeta?.keyLabel ? { keyLabel: pasteMeta.keyLabel } : {}),
      ...(pasteMeta?.privateKeyPath ? { privateKeyPath: pasteMeta.privateKeyPath } : {}),
    });
    return response as unknown as SSHUserCertResponse;
  },

  // ==========================================
  // MQTT Tunnel Lifecycle Management
  // (Extended timeouts: MQTT commands wait for edge response)
  // ==========================================

  // The axios interceptor in api.ts already returns response.data — these
  // endpoints reply with a flat JSON body, so we return it as-is.

  /**
   * Start SSH tunnel via MQTT command
   */
  startSSHTunnel: async (guid: string) => {
    return await api.post(`/api/v2/edges/${guid}/ssh-tunnel/start`, {}, { timeout: 35000 });
  },

  /**
   * Restart SSH tunnel via MQTT command
   */
  restartSSHTunnel: async (guid: string) => {
    return await api.post(`/api/v2/edges/${guid}/ssh-tunnel/restart`, {}, { timeout: 35000 });
  },

  /**
   * Stop SSH tunnel via MQTT command
   */
  stopSSHTunnel: async (guid: string) => {
    return await api.post(`/api/v2/edges/${guid}/ssh-tunnel/stop`, {}, { timeout: 35000 });
  },

  /**
   * Get SSH tunnel health check via MQTT
   */
  getSSHTunnelHealthCheck: async (guid: string) => {
    return await api.post(`/api/v2/edges/${guid}/ssh-tunnel/health-check`, {}, { timeout: 25000 });
  },

  /**
   * Get real-time SSH tunnel status via MQTT
   */
  getSSHTunnelMQTTStatus: async (guid: string) => {
    return await api.get(`/api/v2/edges/${guid}/ssh-tunnel/mqtt-status`, { timeout: 15000 });
  },

  // ==========================================
  // Tunnel Telemetry & Metrics
  // ==========================================

  /**
   * Get SSH tunnel metrics from Victoria Metrics
   */
  getSSHTunnelMetrics: async (guid: string, timeRange?: string) => {
    const params = timeRange ? { timeRange } : {};
    const response = await api.get(`/api/v2/edges/${guid}/ssh-tunnel/metrics`, { params });
    return response.data;
  },

  /**
   * Get SSH tunnel health status (derived from metrics)
   */
  getSSHTunnelHealth: async (guid: string, options?: RequestOptions) => {
    const response = await api.get(`/api/v2/edges/${guid}/ssh-tunnel/health`, options);
    return response as unknown as any; // Axios interceptor already extracts .data
  },

  // ==========================================
  // Phase 3: Key Rotation
  // ==========================================

  /**
   * Rotate SSH tunnel key for an edge
   */
  rotateSSHTunnelKey: async (guid: string) => {
    return await api.post(`/api/v2/edges/${guid}/ssh-tunnel/rotate-key`);
  },

  /**
   * Reinitialize SSH tunnel — re-push keys from DB to edge via MQTT.
   * Generates new key if none exists. Returns immediately (fire-and-forget).
   */
  reinitializeSSHTunnel: async (guid: string) => {
    const response = await api.post(`/api/v2/edges/${guid}/ssh-tunnel/reinitialize`);
    return response.data;
  },

  /**
   * Get key rotation history
   */
  getKeyRotationHistory: async (guid: string, limit: number = 10) => {
    const response = await api.get(`/api/v2/edges/${guid}/ssh-tunnel/rotation-history`, {
      params: { limit },
    });
    return response;
  },

  // ==========================================
  // Phase 3: Alerts
  // ==========================================

  /**
   * Get alerts for a specific edge
   */
  getEdgeAlerts: async (guid: string) => {
    const response = await api.get(`/api/v2/edges/${guid}/alerts`);
    return response.data;
  },

  /**
   * Get all active alerts
   */
  getAllAlerts: async () => {
    const response = await api.get('/api/v2/edges/alerts');
    return response.data;
  },

  /**
   * Acknowledge an alert
   */
  acknowledgeAlert: async (alertId: number) => {
    const response = await api.post(`/api/v2/edges/alerts/${alertId}/acknowledge`);
    return response.data;
  },

  // ==========================================
  // Phase 3: Bulk Operations
  // ==========================================

  /**
   * Create a bulk operation
   */
  createBulkOperation: async (operation: string, filter: any) => {
    const response = await api.post('/api/v2/edges/bulk-operations', {
      operation,
      filter,
    });
    return response.data;
  },

  /**
   * Get bulk operation status
   */
  getBulkOperation: async (operationId: string) => {
    const response = await api.get(`/api/v2/edges/bulk-operations/${operationId}`);
    return response.data;
  },

  /**
   * Get all recent bulk operations
   */
  getAllBulkOperations: async (limit: number = 50) => {
    const response = await api.get('/api/v2/edges/bulk-operations', {
      params: { limit },
    });
    return response.data;
  },

  // ==========================================
  // ACL (Access Control Lists)
  // ==========================================

  /**
   * List ACLs for edge
   */
  listACLs: async (id: string) => {
    const response = await api.get(`/api/v2/edges/${id}/acl`);
    return response.data;
  },

  /**
   * Create ACL
   */
  createACL: async (id: string, acl: any) => {
    const response = await api.post(`/api/v2/edges/${id}/acl`, acl);
    return response.data;
  },

  /**
   * Delete ACL
   */
  deleteACL: async (id: string, aclName: string) => {
    await api.delete(`/api/v2/edges/${id}/acl/${aclName}`);
  },

  // ==========================================
  // QoS (Quality of Service)
  // ==========================================

  /**
   * List QoS policies for edge
   */
  listQoSPolicies: async (id: string) => {
    const response = await api.get(`/api/v2/edges/${id}/qos/policies`);
    return response.data;
  },

  /**
   * Create QoS policy
   */
  createQoSPolicy: async (id: string, policy: any) => {
    const response = await api.post(`/api/v2/edges/${id}/qos/policies`, policy);
    return response.data;
  },

  /**
   * Delete QoS policy
   */
  deleteQoSPolicy: async (id: string, policyId: string) => {
    await api.delete(`/api/v2/edges/${id}/qos/policies/${policyId}`);
  },

  // ==========================================
  // VPP Status & Observability
  // ==========================================

  /**
   * Get latest VPP status confirmation from edge
   */
  getLatestStatus: async (id: string) => {
    const response = await api.get(`/edges/${id}/status/latest`);
    return response.data;
  },

  // Migration 0094: getStatusHistory removed. The /status/history
  // endpoint was retired when edge_status_confirmations switched to
  // UPSERT (one row per edge). No UI consumer was calling this method.

  /**
   * Request on-demand status update from edge via MQTT
   */
  requestStatus: async (id: string) => {
    const response = await api.post(`/edges/${id}/status/request`);
    return response.data;
  },
};

// SSH types
export interface SSHStatusResponse {
  tunnelKeyExists: boolean;
  tunnelActive: boolean;
  tunnelPort: number;
  hostCertInstalled: boolean;
  bastionHost: string;
  bastionPort: number;
  caPublicKey: string;
}

export interface SSHTunnelKeyResponse {
  privateKey: string;
  publicKey: string;
  authorizedKey: string;
  bastionHost: string;
  bastionPort: number;
  tunnelPort: number;
  mqttDeployed: boolean; // Always true — MQTT push is fire-and-forget
}

export interface SSHHostCertResponse {
  certificate: string;
  publicKey: string;
  principals: string[];
  validAfter: number;
  validBefore: number;
  caPublicKey: string;
}

export interface SSHUserCertResponse {
  certificate: string;
  publicKey: string;
  principals: string[];
  validAfter: number;
  validBefore: number;
  // VSNP-680 UX follow-up: where to point IdentityFile in the rendered
  // ~/.ssh/config block. Always populated (per-algo default if no override).
  userKeyId?: string;
  privateKeyPath: string;
}

export default edgeService;
